import { useState, useEffect } from 'react'
import { Calendar, Clock, MapPin, Video, Edit3, Check, X, ChevronDown } from 'lucide-react'
import { useCaseStore } from '../stores'
import { useI18n } from '../i18n'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#46A8F9'

/**
 * AppointmentEntry — collapsible card for entering appointment date/time
 * Appears on DiagnoseScreen after ExternalDoctorLookup when doctor is saved.
 * Patient can return anytime to add/edit appointment details.
 * 
 * Persistence: appointments table is the single source of truth.
 * On load, finds the manual_external appointment from currentCase.appointments[].
 * No diagnoseData dependency — avoids auto-save debounce issues.
 */
export default function AppointmentEntry({ savedDoctor, currentCase, onAppointmentSaved }) {
  const { t } = useI18n()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [savedAppointment, setSavedAppointment] = useState(null)
  
  const [form, setForm] = useState({
    date: '',
    time: '',
    type: 'in_person',
  })

  // Load existing appointment from the appointments array on the case
  // The appointments table is the single source of truth — no diagnoseData needed
  useEffect(() => {
    const appts = currentCase?.appointments || []
    const ext = appts.find(a => a.source === 'manual_external')
    if (ext) {
      setSavedAppointment(ext)
      setForm({
        date: ext.date || '',
        time: ext.time || '',
        type: ext.type || 'in_person',
      })
    }
  }, [currentCase?.id, currentCase?.appointments?.length])

  const handleSave = async () => {
    if (!form.date) return
    setIsSaving(true)
    setSaveError(null)

    try {
      const { appointmentsAPI } = await import('../services/api')

      const payload = {
        case_id: currentCase.id,
        date: form.date,
        time: form.time || '09:00',
        type: form.type,
        status: 'scheduled',
        source: 'manual_external',
        scheduled_by: 'patient',
        doctor_name: savedDoctor?.name || '',
        practice_name: savedDoctor?.name || '',
        practice_address: savedDoctor?.address || '',
        practice_phone: savedDoctor?.phone || '',
        specialty: currentCase?.specialtyName || currentCase?.specialty || '',
        focus_area: currentCase?.focusAreaName || currentCase?.focus_area || '',
        notes: 'Added by patient — external doctor visit',
      }

      let resultAppt = null
      if (savedAppointment?.id) {
        await appointmentsAPI.update(savedAppointment.id, {
          date: form.date,
          time: form.time || '09:00',
          type: form.type,
        })
        resultAppt = { ...savedAppointment, date: form.date, time: form.time || '09:00', type: form.type }
      } else {
        const result = await appointmentsAPI.create(payload)
        resultAppt = result?.appointment || payload
      }

      setSavedAppointment(resultAppt)
      console.log('[AppointmentEntry] ✅ Saved to appointments table')

      // Reload cases to refresh the appointments array from API
      try {
        const { loadCases } = useCaseStore.getState()
        await loadCases()
        console.log('[AppointmentEntry] ✅ Cases reloaded')
      } catch (reloadErr) {
        console.warn('[AppointmentEntry] Cases reload warning:', reloadErr)
      }

      setIsExpanded(false)
      onAppointmentSaved?.(resultAppt)
    } catch (err) {
      console.error('[AppointmentEntry] Save failed:', err)
      setSaveError(t('errors2.failedToSaveAppointment'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!savedAppointment?.id) {
      setSavedAppointment(null)
      setForm({ date: '', time: '', type: 'in_person' })
      return
    }

    try {
      const { appointmentsAPI } = await import('../services/api')
      await appointmentsAPI.delete(savedAppointment.id)
      setSavedAppointment(null)
      setForm({ date: '', time: '', type: 'in_person' })
      setIsExpanded(false)
      onAppointmentSaved?.(null)

      // Reload cases to refresh
      try {
        const { loadCases } = useCaseStore.getState()
        await loadCases()
      } catch (reloadErr) {
        console.warn('[AppointmentEntry] Cases reload warning:', reloadErr)
      }
    } catch (err) {
      console.error('[AppointmentEntry] Delete failed:', err)
      setSaveError(t('errors2.couldNotRemoveAppt'))
    }
  }

  // Format helpers
  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    try {
      const [y, m, d] = dateStr.split('-')
      // PROD-1278: a date-only value is a calendar day, not an instant. Build it at UTC noon and
      // format it in UTC so every device time zone shows the same day (local midnight formatted in
      // Los Angeles time showed the previous day on phones east of Pacific time).
      const date = new Date(Date.UTC(parseInt(y), parseInt(m) - 1, parseInt(d), 12))
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    } catch { return dateStr }
  }

  const formatTime = (timeStr) => {
    if (!timeStr) return ''
    try {
      const [h, m] = timeStr.split(':')
      const hour = parseInt(h)
      const ampm = hour >= 12 ? 'PM' : 'AM'
      const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
      return `${h12}:${m} ${ampm}`
    } catch { return timeStr }
  }

  // Don't render if no doctor is saved
  if (!savedDoctor) return null

  // ===== SAVED STATE — compact appointment card =====
  if (savedAppointment?.date && !isExpanded) {
    return (
      <div className="bg-white rounded-2xl border-[1.5px] border-blue-200 overflow-hidden">
        <div className="flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] border-l-[#46A8F9] bg-[#E9F8FF]">
          <Calendar size={15} className="text-[#46A8F9]" />
          <h4 className="text-[12px] font-bold uppercase tracking-[0.5px] text-[#15354F]">{t('appointment.yourAppointment')}</h4>
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => setIsExpanded(true)}
              className="p-1 text-[#46A8F9] active:text-blue-700"
            >
              <Edit3 size={13} />
            </button>
            <button
              onClick={handleDelete}
              className="p-1 text-red-400 active:text-red-600"
            >
              <X size={13} />
            </button>
          </div>
        </div>
        <div className="px-3.5 py-3 space-y-1.5">
          <div className="flex items-center gap-2">
            <Calendar size={13} className="text-gray-400" />
            <p className="text-[13px] font-bold" style={{ color: NAVY }}>{formatDate(savedAppointment.date)}</p>
          </div>
          {savedAppointment.time && (
            <div className="flex items-center gap-2">
              <Clock size={13} className="text-gray-400" />
              <p className="text-[12px] text-gray-600">{formatTime(savedAppointment.time)}</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            {(savedAppointment.type === 'video' || savedAppointment.type === 'telehealth') ? (
              <>
                <Video size={13} className="text-[#2D8CFF]" />
                <p className="text-[12px] text-gray-500">{t('appointment.telehealth')}</p>
              </>
            ) : (
              <>
                <MapPin size={13} className="text-gray-400" />
                <p className="text-[12px] text-gray-500">{t('appointment.inperson')}</p>
              </>
            )}
          </div>
          <p className="text-[10px] text-gray-400 mt-1">{t('appointment.visibleOnYourMyVisits')}</p>
        </div>
      </div>
    )
  }

  // ===== EMPTY / EDIT STATE — entry form =====
  return (
    <div className="bg-white rounded-2xl border-[1.5px] border-gray-200 overflow-hidden">
      {/* Header — tappable to expand/collapse */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] border-l-[#46A8F9] bg-[#E9F8FF] text-left"
      >
        <Calendar size={15} className="text-[#46A8F9]" />
        <h4 className="text-[12px] font-bold uppercase tracking-[0.5px] text-[#15354F]">
          {savedAppointment?.date ? 'Edit Appointment' : 'Add Your Appointment'}
        </h4>
        <span className="ml-auto text-[9px] font-semibold text-gray-400">{t('appointment.optional')}</span>
        <ChevronDown 
          size={14} 
          className="text-gray-400 transition-transform" 
          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }} 
        />
      </button>

      {isExpanded && (
        <div className="p-3.5 space-y-3">
          <p className="text-[11.5px] text-gray-400 leading-relaxed">
            {t('appointment.addDetailsNote', 'Add your appointment details and it will appear on your My Visits page.')}
          </p>

          {/* Date */}
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 mb-1">{t('appointment.date')}</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border-[1.5px] border-gray-200 text-[13px] focus:outline-none focus:border-[#46A8F9] transition-colors"
              style={{ color: NAVY }}
            />
          </div>

          {/* Time */}
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 mb-1">{t('appointment.time')}</label>
            <input
              type="time"
              value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border-[1.5px] border-gray-200 text-[13px] focus:outline-none focus:border-[#46A8F9] transition-colors"
              style={{ color: NAVY }}
            />
          </div>

          {/* Visit Type Toggle */}
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 mb-1.5">{t('appointment.visitType')}</label>
            <div className="flex gap-2">
              <button
                onClick={() => setForm({ ...form, type: 'in_person' })}
                className="flex-1 py-2.5 rounded-xl text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-all border-[1.5px]"
                style={{
                  backgroundColor: form.type === 'in_person' ? '#E9F8FF' : 'white',
                  borderColor: form.type === 'in_person' ? '#46A8F9' : '#E5E7EB',
                  color: form.type === 'in_person' ? NAVY : '#9CA3AF',
                }}
              >
                <MapPin size={14} />
                {t('caseHome.inPerson')}
              </button>
              <button
                onClick={() => setForm({ ...form, type: 'video' })}
                className="flex-1 py-2.5 rounded-xl text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-all border-[1.5px]"
                style={{
                  backgroundColor: form.type === 'video' ? '#E9F8FF' : 'white',
                  borderColor: form.type === 'video' ? '#46A8F9' : '#E5E7EB',
                  color: form.type === 'video' ? NAVY : '#9CA3AF',
                }}
              >
                <Video size={14} />
                {t('appointment.telehealth', 'Telehealth')}
              </button>
            </div>
          </div>

          {/* Error */}
          {saveError && (
            <p className="text-[11px] text-red-500 bg-red-50 rounded-lg px-3 py-2">{saveError}</p>
          )}

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={!form.date || isSaving}
            className="w-full py-3 rounded-xl font-bold text-[13px] flex items-center justify-center gap-2 transition-all"
            style={{
              backgroundColor: form.date ? BLUE : '#F3F4F6',
              color: form.date ? 'white' : '#9CA3AF',
            }}
          >
            {isSaving ? (
              <OrellaSpinner size={16} tone="inherit" />
            ) : (
              <>
                <Check size={16} strokeWidth={2.5} />
                {savedAppointment?.date ? 'Update Appointment' : 'Save Appointment'}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  )
}
